import {
  keepPreviousData,
  type QueryKey,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import { api } from '../../utils/apiService';
import {
  type ApiInquiry,
  type ApiSummary,
  fromApi,
  fromApiSummary,
} from './adapter';
import { appendNote } from './form';
import type { InquiryKindConfig } from './kind';
import {
  type DashboardState,
  type EditableFields,
  type InquiryRecord,
  type ListQuery,
  type SummaryCounts,
  PAGE_SIZE,
  toListParams,
} from './model';

export const POLL_MS = 60_000;

async function readError(response: Response | null, fallback: string) {
  if (!response) return fallback;
  const body = (await response.json().catch(() => null)) as Record<
    string,
    unknown
  > | null;
  if (!body) return fallback;
  if (typeof body.detail === 'string') return body.detail;
  const first = Object.values(body)[0];
  if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
  return fallback;
}

async function getJson<T>(url: string, fallback: string): Promise<T> {
  const response = await api.get(url);
  if (!response?.ok) throw new Error(await readError(response, fallback));
  return response.json();
}

async function patchJson<T>(
  url: string,
  body: unknown,
  fallback: string,
): Promise<T> {
  const response = await api.patch(url, body);
  if (!response?.ok) throw new Error(await readError(response, fallback));
  return response.json();
}

export interface ListResult {
  items: InquiryRecord[];
  totalCount: number;
  totalPages: number;
}

type ApiList =
  | {
      results?: ApiInquiry[];
      count?: number;
      total_pages?: number;
    }
  | ApiInquiry[];

export async function fetchInquiries(
  config: InquiryKindConfig,
  q: ListQuery,
): Promise<ListResult> {
  const data = await getJson<ApiList>(
    config.endpoints.list(toListParams(q)),
    'Inquiries could not be loaded.',
  );
  const rows = Array.isArray(data) ? data : (data.results ?? []);
  const totalCount = Array.isArray(data)
    ? rows.length
    : (data.count ?? rows.length);
  const totalPages = Array.isArray(data)
    ? 1
    : (data.total_pages ?? Math.max(1, Math.ceil(totalCount / PAGE_SIZE)));
  return { items: rows.map(fromApi), totalCount, totalPages };
}

function useInquiryKeys(config: InquiryKindConfig) {
  const lists = useScopedQueryKey(`${config.kind}-inquiries`);
  const summary = useScopedQueryKey(`${config.kind}-inquiry-summary`);
  const detail = useScopedQueryKey(`${config.kind}-inquiry`);
  return useMemo(
    () => ({
      lists: lists as QueryKey,
      summary: summary as QueryKey,
      detail: (id: number) => [...detail, id] as QueryKey,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(lists), JSON.stringify(summary), JSON.stringify(detail)],
  );
}

export function useInquiryList(config: InquiryKindConfig, q: ListQuery) {
  const keys = useInquiryKeys(config);
  return useQuery({
    queryKey: [...keys.lists, q],
    queryFn: () => fetchInquiries(config, q),
    placeholderData: keepPreviousData,
    refetchInterval: POLL_MS,
  });
}

export function useInquirySummary(config: InquiryKindConfig) {
  const keys = useInquiryKeys(config);
  return useQuery<SummaryCounts>({
    queryKey: keys.summary,
    queryFn: async () =>
      fromApiSummary(
        await getJson<ApiSummary>(
          config.endpoints.summary(),
          'Counts could not be loaded.',
        ),
      ),
    refetchInterval: POLL_MS,
  });
}

export function useInquiry(config: InquiryKindConfig, id: number | null) {
  const keys = useInquiryKeys(config);
  return useQuery({
    queryKey: keys.detail(id ?? 0),
    queryFn: async () =>
      fromApi(
        await getJson<ApiInquiry>(
          config.endpoints.detail(id as number),
          'This inquiry could not be loaded.',
        ),
      ),
    enabled: id != null,
  });
}

/**
 * Writes. Every one returns the detail serializer, which replaces the cached
 * record; lists and counts refetch behind it.
 */
export function useInquiryActions(config: InquiryKindConfig) {
  const queryClient = useQueryClient();
  const keys = useInquiryKeys(config);

  const settle = useCallback(
    async (record: InquiryRecord) => {
      await queryClient.cancelQueries({ queryKey: keys.detail(record.id) });
      queryClient.setQueryData(keys.detail(record.id), record);
      queryClient.setQueriesData<ListResult>(
        { queryKey: keys.lists },
        (current) =>
          current && {
            ...current,
            items: current.items.map((r) => (r.id === record.id ? record : r)),
          },
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: keys.lists }),
        queryClient.invalidateQueries({ queryKey: keys.summary }),
        config.kind === 'event'
          ? queryClient.invalidateQueries({ queryKey: ['nav-unseen-events'] })
          : Promise.resolve(),
      ]);
      return record;
    },
    [queryClient, keys],
  );

  /** PATCH …/:id/ with only the changed editable fields. */
  const update = useCallback(
    async (id: number, fields: EditableFields) =>
      settle(
        fromApi(
          await patchJson<ApiInquiry>(
            config.endpoints.update(id),
            fields,
            'Changes could not be saved.',
          ),
        ),
      ),
    [config, settle],
  );

  /** PATCH …/:id/dashboard-state/ { dashboard_state }. */
  const setState = useCallback(
    async (id: number, state: DashboardState) =>
      settle(
        fromApi(
          await patchJson<ApiInquiry>(
            config.endpoints.dashboardState(id),
            { dashboard_state: state },
            'The inquiry could not be updated.',
          ),
        ),
      ),
    [config, settle],
  );

  /**
   * Appends to the latest `follow_up_notes` (re-read first, so a note added
   * in another tab isn't overwritten).
   */
  const addNote = useCallback(
    async (id: number, text: string, author: string) => {
      const current = fromApi(
        await getJson<ApiInquiry>(
          config.endpoints.detail(id),
          'The note could not be saved.',
        ),
      );
      return update(id, {
        follow_up_notes: appendNote(current.followUpNotes, text, author),
      });
    },
    [config, update],
  );

  /** POST …/mark-all-read/ — unseen inquiries become seen. */
  const markAllRead = useCallback(async () => {
    const response = await api.post(config.endpoints.markAllRead());
    if (!response?.ok) {
      throw new Error(await readError(response, 'Could not mark all as read.'));
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: keys.lists }),
      queryClient.invalidateQueries({ queryKey: keys.summary }),
      queryClient.invalidateQueries({
        queryKey: keys.detail(0).slice(0, -1),
      }),
      config.kind === 'event'
        ? queryClient.invalidateQueries({ queryKey: ['nav-unseen-events'] })
        : Promise.resolve(),
    ]);
  }, [config, queryClient, keys]);

  return useMemo(
    () => ({ update, setState, addNote, markAllRead }),
    [update, setState, addNote, markAllRead],
  );
}
